import React, { Fragment, useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Table, Badge } from "reactstrap";
import { useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import Breadcrumbs from "../../../layout/breadcrumb";
import PreLoader from "../../../share-components/ui/preLoader";
import withReducer from "../../../store/withReducer";
import orderReducer from "../store";
import { getEuOrder } from "../store/orderSlice";

const dataMenu = [
  { type: 1, text: "ASOS EU Orders", link: `${process.env.PUBLIC_URL}/order/eu-orders` },
  { type: 0, text: "Order View" },
];

const STATE_COLORS = {
  SHIPPING: 'primary', SHIPPED: 'success', CLOSED: 'dark',
  CANCELED: 'danger', RECEIVED: 'info',
};

function EuOrderView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(getEuOrder(id)).then((res) => {
      if (res.payload) setOrder(res.payload);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs title="Order View" data={dataMenu} />
        <Container fluid>
          <div style={{ minHeight: 400 }}><PreLoader /></div>
        </Container>
      </Fragment>
    );
  }

  if (!order) {
    return (
      <Fragment>
        <Breadcrumbs title="Order View" data={dataMenu} />
        <Container fluid>
          <p className="text-danger mt-4">Order not found.</p>
        </Container>
      </Fragment>
    );
  }

  const rate = parseFloat(order.currency_rate || 1);
  const toGBP = (val) => (parseFloat(val || 0) / rate).toFixed(2);
  const shippingAddr = order.shipping_address || {};
  const billingAddr  = order.billing_address  || {};

  const cancelledItems = (order.line_items || []).filter(
    (i) => i.order_line_state === 'CANCELED'
  );

  return (
    <Fragment>
      <Breadcrumbs title={`Order: ${order.mirakl_order_number}`} data={dataMenu} />

      <Container fluid className="product-wrapper">

        {/* Order Summary */}
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Order Details</h5>
              <button className="btn btn-sm btn-light" onClick={() => history.goBack()}>← Back</button>
            </div>
            <Row>
              <Col md={3}><small className="text-muted">Mirakl Order No</small><p className="fw-bold mb-1">{order.mirakl_order_number}</p></Col>
              <Col md={3}><small className="text-muted">Shopify Order</small><p className="mb-1">{order.shopify_order_name || '—'}</p></Col>
              <Col md={3}><small className="text-muted">Order Date</small><p className="mb-1">{order.order_date}</p></Col>
              <Col md={3}><small className="text-muted">Shipping Deadline</small><p className="mb-1">{order.shipping_deadline || '—'}</p></Col>
              <Col md={3}><small className="text-muted">Currency</small><p className="mb-1">{order.currency}</p></Col>
              <Col md={3}><small className="text-muted">Currency Rate</small><p className="mb-1">{order.currency_rate}</p></Col>
              <Col md={3}><small className="text-muted">Total Price (GBP)</small><p className="mb-1">{toGBP(order.total_price)}</p></Col>
              <Col md={3}><small className="text-muted">Country Code</small><p className="mb-1">{order.country_code}</p></Col>
              <Col md={3}><small className="text-muted">Channel</small><p className="mb-1">{order.channel_label || order.country_code}</p></Col>
              <Col md={3}><small className="text-muted">Fulfilment</small><p className="mb-1">{order.fulfilment_status}</p></Col>
              <Col md={3}><small className="text-muted">Sync Status</small><p className="mb-1">{order.sync_status}</p></Col>
              <Col md={3}><small className="text-muted">Order State</small><p className="mb-1">
                <Badge color={STATE_COLORS[order.order_state] || 'secondary'}>{order.order_state}</Badge>
              </p></Col>
            </Row>
            {order.picking_ticket_url && (
              <div className="mt-2">
                <a href={order.picking_ticket_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary">
                  Picking Ticket
                </a>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Customer / Addresses */}
        <Row className="mb-3">
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Customer</h6>
                {order.customer?.email ? (
                  <>
                    <p className="mb-1">{order.customer.first_name} {order.customer.last_name}</p>
                    <p className="mb-0 text-muted">{order.customer.email}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Shipping Address</h6>
                {shippingAddr.firstname ? (
                  <>
                    <p className="mb-1">{shippingAddr.firstname} {shippingAddr.lastname}</p>
                    <p className="mb-1">{shippingAddr.street_1}{shippingAddr.street_2 ? `, ${shippingAddr.street_2}` : ''}</p>
                    <p className="mb-1">{shippingAddr.zip_code} {shippingAddr.city}</p>
                    <p className="mb-0">{shippingAddr.country}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Billing Address</h6>
                {billingAddr.firstname ? (
                  <>
                    <p className="mb-1">{billingAddr.firstname} {billingAddr.lastname}</p>
                    <p className="mb-1">{billingAddr.street_1}{billingAddr.street_2 ? `, ${billingAddr.street_2}` : ''}</p>
                    <p className="mb-1">{billingAddr.zip_code} {billingAddr.city}</p>
                    <p className="mb-0">{billingAddr.country}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Line Items */}
        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">Line Items ({order.line_items?.length || 0})</h6>
            <div className="table-responsive">
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>SKU</th>
                    <th>Product Title</th>
                    <th>Qty</th>
                    <th>Price (GBP)</th>
                    <th>Total (GBP)</th>
                    <th>Commission</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {order.line_items?.length ? order.line_items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.sku || <span className="text-danger fw-bold">Not Found</span>}</td>
                      <td>{item.product_title}</td>
                      <td>{item.quantity}</td>
                      <td>{toGBP(item.price)}</td>
                      <td>{toGBP(item.total_price)}</td>
                      <td>{toGBP(item.commission_fee)}</td>
                      <td>
                        <span className={`badge bg-${item.order_line_state === 'CANCELED' ? 'danger' : item.order_line_state === 'SHIPPED' ? 'success' : 'secondary'}`}>
                          {item.order_line_state}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} className="text-center text-muted">No line items</td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Totals */}
            {(() => {
              const items = order.line_items || [];
              const total = items.reduce((s, i) => s + parseFloat(i.total_price || 0), 0) / rate;
              const commission = items.reduce((s, i) => s + parseFloat(i.total_commission || 0), 0) / rate;
              const shipping = parseFloat(order.shipping_price || 0) / rate;
              return (
                <div className="d-flex justify-content-end mt-3">
                  <table style={{ minWidth: 280 }}>
                    <tbody>
                      <tr><td className="text-muted pe-4">Items Total</td><td className="text-end fw-bold">GBP {total.toFixed(2)}</td></tr>
                      <tr><td className="text-muted pe-4">Commission</td><td className="text-end fw-bold">GBP {commission.toFixed(2)}</td></tr>
                      <tr><td className="text-muted pe-4">Shipping</td><td className="text-end fw-bold">GBP {shipping.toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CardBody>
        </Card>

        {/* Refunds */}
        {order.refunds?.length > 0 && (
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-3">Refunds ({order.refunds.length})</h6>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>SKU</th>
                      <th>Qty</th>
                      <th>Amount (GBP)</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.refunds.map((r) => (
                      <tr key={r.id}>
                        <td>{r.sku || '—'}</td>
                        <td>{r.quantity}</td>
                        <td>{toGBP(r.amount)}</td>
                        <td>{r.created_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Cancelations */}
        {cancelledItems.length > 0 && (
          <Card>
            <CardBody>
              <h6 className="mb-3">Cancelled Line Items ({cancelledItems.length})</h6>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>SKU</th>
                      <th>Product Title</th>
                      <th>Qty</th>
                      <th>Price (GBP)</th>
                      <th>Total (GBP)</th>
                      <th>Cancelations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cancelledItems.map((item) => (
                      <tr key={item.id}>
                        <td>{item.sku || '—'}</td>
                        <td>{item.product_title}</td>
                        <td>{item.quantity}</td>
                        <td>{toGBP(item.price)}</td>
                        <td>{toGBP(item.total_price)}</td>
                        <td>
                          {item.cancelations?.length > 0 ? (
                            <ul className="mb-0 ps-3">
                              {item.cancelations.map((c) => (
                                <li key={c.id}>
                                  Qty: {c.quantity} / Amount: GBP {toGBP(c.amount)} / {c.created_date}
                                </li>
                              ))}
                            </ul>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

      </Container>
    </Fragment>
  );
}

export default withReducer([{ order: orderReducer }])(EuOrderView);
